// Recorder: attaches capture-phase listeners to a document and turns user
// interactions into a serialisable list of steps. Framework-agnostic; pass an
// explicit { win, doc } (defaults to the ambient browser globals).
//
// Step shapes (all carry `target` = a selector descriptor, and `delay` ms since
// the previous step):
//   { action:'fill',   target, value }        text/number/textarea input
//   { action:'check',  target, value:bool }   checkbox / radio
//   { action:'select', target, value }        <select>
//   { action:'click',  target }               buttons/links/labels
//   { action:'navigate', url }                pushState/replaceState/hash change

const { describe, sameTarget } = require('./selector');

const MAX_DELAY_MS = 10000;

class Recorder {
    constructor(opts) {
        opts = opts || {};
        this.win = opts.win || (typeof window !== 'undefined' ? window : null);
        this.doc = opts.doc || (this.win && this.win.document) || (typeof document !== 'undefined' ? document : null);
        // Events originating inside the tester's own UI (chat panel, record
        // controls) must NOT be recorded. Default matches the widget chrome
        // marker; callers can override/extend.
        this.ignore = opts.ignore || '[data-lyric-tester-ui]';
        this.steps = [];
        this._handlers = [];
        this._recording = false;
        this._lastTs = 0;
        this._patched = null;
    }

    // True if the event target lives inside the tester's own UI chrome.
    _isOwnUi(target) {
        if (!this.ignore || !target || !target.closest) return false;
        try {
            return !!target.closest(this.ignore);
        } catch (_) {
            return false;
        }
    }

    _push(step) {
        const now = Date.now();
        step.delay = this._lastTs ? Math.min(now - this._lastTs, MAX_DELAY_MS) : 0;
        this._lastTs = now;
        this.steps.push(step);
        return step;
    }

    _on(type, fn) {
        this.doc.addEventListener(type, fn, true); // capture phase — see everything
        this._handlers.push([type, fn]);
    }

    start() {
        if (this._recording) return this;
        this._recording = true;
        this.steps = [];
        this._lastTs = 0;

        this._on('click', (e) => {
            const t = e.target;
            if (this._isOwnUi(t)) return;
            const el = (t.closest && t.closest(
                'a,button,[role=button],input[type=submit],input[type=button],input[type=reset],' +
                'input[type=checkbox],input[type=radio],label,summary,[data-testid],[onclick]'
            )) || t;
            // Text inputs / selects are captured via input/change, not click.
            const tag = (el.tagName || '').toLowerCase();
            if (tag === 'input') {
                const type = (el.getAttribute('type') || 'text').toLowerCase();
                if (type === 'checkbox' || type === 'radio') return; // -> change ('check')
                if (!(type === 'submit' || type === 'button' || type === 'reset')) return; // text-like -> 'fill'
            } else if (tag === 'textarea' || tag === 'select') {
                return;
            }
            this._push({ action: 'click', target: describe(this.win, el) });
        });

        this._on('input', (e) => {
            const el = e.target;
            if (this._isOwnUi(el)) return;
            const tag = (el.tagName || '').toLowerCase();
            if (tag !== 'input' && tag !== 'textarea') return;
            const type = (el.getAttribute('type') || 'text').toLowerCase();
            if (type === 'checkbox' || type === 'radio') return; // -> change
            const tgt = describe(this.win, el);
            const last = this.steps[this.steps.length - 1];
            if (last && last.action === 'fill' && sameTarget(last.target, tgt)) {
                last.value = el.value; // collapse keystrokes into one fill
                return;
            }
            this._push({ action: 'fill', target: tgt, value: el.value });
        });

        this._on('change', (e) => {
            const el = e.target;
            if (this._isOwnUi(el)) return;
            const tag = (el.tagName || '').toLowerCase();
            const type = (el.getAttribute('type') || '').toLowerCase();
            if (type === 'checkbox' || type === 'radio') {
                this._push({ action: 'check', target: describe(this.win, el), value: !!el.checked });
                return;
            }
            if (tag === 'select') {
                this._push({ action: 'select', target: describe(this.win, el), value: el.value });
            }
        });

        // In-app navigation: patch the History API + listen to pop/hash so SPA
        // route changes become replayable steps.
        this._patchHistory();

        return this;
    }

    _patchHistory() {
        const win = this.win;
        if (!win || !win.history) return;
        const record = () => {
            try {
                this._push({ action: 'navigate', url: String(win.location.pathname + win.location.search + win.location.hash) });
            } catch (_) { /* noop */ }
        };
        const origPush = win.history.pushState;
        const origReplace = win.history.replaceState;
        const wrap = (orig) => function () {
            const r = orig.apply(this, arguments);
            record();
            return r;
        };
        try {
            win.history.pushState = wrap(origPush);
            win.history.replaceState = wrap(origReplace);
        } catch (_) { /* some sandboxes freeze history */ }
        const onPop = () => record();
        const onHash = () => record();
        win.addEventListener('popstate', onPop);
        win.addEventListener('hashchange', onHash);
        this._patched = { origPush, origReplace, onPop, onHash };
    }

    stop() {
        for (const [type, fn] of this._handlers) {
            this.doc.removeEventListener(type, fn, true);
        }
        this._handlers = [];
        if (this._patched && this.win) {
            const win = this.win;
            try {
                win.history.pushState = this._patched.origPush;
                win.history.replaceState = this._patched.origReplace;
            } catch (_) { /* noop */ }
            win.removeEventListener('popstate', this._patched.onPop);
            win.removeEventListener('hashchange', this._patched.onHash);
            this._patched = null;
        }
        this._recording = false;
        return this.steps.slice();
    }

    isRecording() {
        return this._recording;
    }
}

module.exports = { Recorder };
